import { trackEvent } from '../utils/analytics';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from 'react-error-boundary';
import type { ErrorInfo } from 'react';

function ErrorFallback({ resetErrorBoundary }: FallbackProps) {
  const { t } = useTranslation();

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center bg-gray-50 p-4 text-center" role="alert">
      <div className="max-w-lg rounded-xl bg-white p-8 shadow-lg">
        <h2 className="mb-4 text-2xl font-bold text-red-600">{t('errorBoundary.title')}</h2>
        <p className="mb-6 text-gray-700">{t('errorBoundary.description')}</p>
        <button
          onClick={resetErrorBoundary}
          className="rounded-full bg-blue-600 px-6 py-2 font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          {t('errorBoundary.tryAgain')}
        </button>
      </div>
    </div>
  );
}

interface CustomErrorBoundaryProps {
  children: React.ReactNode;
}

export function ErrorBoundary({ children }: CustomErrorBoundaryProps) {
  const handleOnError = (error: unknown, info: ErrorInfo) => {
    const errorObj = error instanceof Error ? error : new Error(String(error));

    trackEvent('js_exception', 'Error', {
      error_message: errorObj.message,
      stack_trace: errorObj.stack,

      component_stack: info.componentStack,
    });
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={handleOnError}
      onReset={() => {
        trackEvent('error_boundary_reset', 'Error', {
          action: 'user_clicked_try_again',
        });
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}
